import { computed, PromiseResourceOptions, resource, ResourceRef, Signal } from "@angular/core";
import { S25Util } from "./s25-util";

export type S25Resource<T> = {
    resource: ResourceRef<T | undefined>;
    value: Signal<T | undefined>;
};

/**
 * Wrapper for the `Promise` version of Angular's `resource`. Right now, it only adds error
 * enforcement to the loader function and exposes a non-throwing `value` signal.
 * TODO: consider expanding `options` to include behavioral changes beyond what resource
 *  offers, e.g. sticky behavior that preserves previous value while loading like in
 *  https://www.reddit.com/r/Angular2/comments/1kwr7db/resource_keep_previous_value_helper/
 */
export function s25Resource<T, TParams>(options: PromiseResourceOptions<T, TParams>): S25Resource<T> {
    const ngResource = resource({
        ...options,
        loader: enforceErrorsOnLoader<T, TParams>(options),
    });
    return {
        resource: ngResource,
        value: computed(() => (ngResource.hasValue() ? ngResource.value() : undefined)),
    };
}

/**
 * Angular expects that promises are rejected with Error instances, rather than strings or objects.
 * If the loader rejects with a non-error, angular will spit out some ugly error messaging,
 * so let's handle it explicitly.
 * thrown error object will be available in error.cause
 */
function enforceErrorsOnLoader<T, R>(options: PromiseResourceOptions<T, R>) {
    return (args: any) => {
        try {
            const promiseLike = options.loader(args);
            if ("catch" in promiseLike && typeof promiseLike.catch === "function") {
                return promiseLike.catch((e: any) => {
                    if (typeof e === "string") {
                        e = new Error(e);
                    } else if (!(e instanceof Error)) {
                        e = new Error(e?.message || S25Util.errorText(e) || "Unknown error", { cause: e });
                    }
                    console.error(...(e.cause ? [e.cause, "Caused: ", e] : [e]));
                    throw e;
                });
            } else {
                return promiseLike;
            }
        } catch (e) {
            console.error(e);
            return Promise.reject(e);
        }
    };
}
